import * as React from 'react'
import { Button, Checkbox, FilterPanel, ICallback, Input, Label, MultiSelect, PortalContainer } from 'uxp/components'
import { IContextProvider } from '../../../uxp'
import { addToPath, findAvailableFields, IPropertyEditorProps, ISerializedUIField, IUIPropertyDefinitionMap, IWDDesignModeProps } from '../common'
import { FieldContainer } from '../FieldContainer'
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames'
import { SelectableItem, selectUIProps } from '../SelectableItem'
import { BooleanProperty } from '../ui-props/BooleanProperty'
import { Property, PropertyContainer, PropertyGroup, PropertyGroupTitle, PropertyTitle } from '../ui-props/common'
import { IDataSourceBinding, executeAndBind } from '../data-source-picker/datasource'
import { DataSourcePropertyEditor } from '../ui-props/DataSourceProperty'
import jp from 'jsonpath'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { generateFullUUID } from '../../../util'
import _ from 'lodash'
import useExportToExcel from '../../../hooks/useExportToExcel'
import { format } from 'date-fns'

interface IWDFilterPanelProps {
    designer: IWDDesignModeProps,
    uxpContext: IContextProvider,
    uiProps?: any,
    containerRef: React.MutableRefObject<any>
    data: { [key: string]: any }
    onChangeFilters: (data: any) => void,
    onClearFilters: () => void,
    onOpenPanel?: () => void,
    onClosePanel?: () => void
    exportFileName?: string
}


interface IWDFilterPanelProperties {
    fillContainer: boolean,
    enableClear: boolean,
    enableExport: boolean
}

interface IExportSheet {
    id: string
    name: string,
    binding: IDataSourceBinding,
    excludeFields?: boolean,
    fieldsToInclude?: string[],
    fieldsToExclude?: string[]
}

const PropertyEditor: React.FunctionComponent<IPropertyEditorProps<IWDFilterPanelProperties>> = (props) => {

    return <PropertyContainer>
        <PropertyGroup>
            <PropertyGroupTitle>Filter Panel</PropertyGroupTitle>

            <Property>
                <Checkbox
                    type='bordered'
                    checked={props.uiProps.fillContainer || false}
                    onChange={s => props.onChange(Object.assign({}, props.uiProps, { fillContainer: s }))}
                    className="widget-designer-input"
                    label='Expand To Fill the widget'
                />
                <Checkbox
                    type='bordered'
                    checked={props.uiProps.enableClear || false}
                    onChange={s => props.onChange(Object.assign({}, props.uiProps, { enableClear: s }))}
                    className="widget-designer-input"
                    label='Enable Clear button'
                />
                <Checkbox
                    type='bordered'
                    checked={props.uiProps.enableExport || false}
                    onChange={s => props.onChange(Object.assign({}, props.uiProps, { enableExport: s }))}
                    className="widget-designer-input"
                    label='Enable Export button'
                />
            </Property>

        </PropertyGroup>
    </PropertyContainer>;
}

const ExportPropertyEditor: React.FunctionComponent<IPropertyEditorProps<{ sheets: IExportSheet[] }>> = (props) => {

    const _props = props.uiProps || { sheets: [] }

    function addSheet() {
        const _sheets = [..._props.sheets]
        _sheets.push({ id: generateFullUUID(), name: `Sheet ${_sheets.length + 1}`, binding: null, excludeFields: false, fieldsToExclude: [], fieldsToInclude: [] })
        props.onChange(Object.assign({}, _props, { sheets: _sheets }))
    }

    return <PropertyContainer>
        <PropertyGroup>
            <PropertyGroupTitle>Export Data</PropertyGroupTitle>

            <Property>
                <PropertyTitle>Configure Data to export</PropertyTitle>

                <div className="uxp-wd-export-sheets-container">
                    {
                        _props?.sheets?.map((s: IExportSheet, i) => {

                            let possibleValues = {};
                            findAvailableFields(s.binding, props?.parentUIProps?.datasources || [], possibleValues);
                            let pv = Object.keys(possibleValues);
                            pv.sort();

                            return <div className="uxp-wd-export-sheet">

                                <div className="uxp-wd-export-sheet-remover"
                                    onClick={e => {
                                        e.preventDefault()
                                        e.stopPropagation()

                                        const filtered = _props.sheets.filter(sheet => sheet.id !== s.id)
                                        props.onChange(Object.assign({}, _props, { sheets: filtered }))
                                    }}
                                >
                                    <FontAwesomeIcon icon={['fas', 'times']} />
                                </div>

                                <Label> Sheet Name </Label>
                                <Input value={s.name} onChange={(s) => {
                                    jp.apply(_props, `$.sheets[${i}].name`, () => s)
                                    props.onChange(_props)
                                }} />

                                <DataSourcePropertyEditor
                                    binding={s.binding}
                                    context={props.uxpContext}
                                    onChange={(binding) => {
                                        jp.apply(_props, `$.sheets[${i}].binding`, () => binding)
                                        props.onChange(_props)
                                    }}
                                    onChangeAny={(p, v) => {
                                        props.onChangeAny(p, v);
                                    }}
                                    sources={props?.parentUIProps?.datasources || []}
                                    target={'dictionary-array'}
                                    title='Data Source'
                                    contextData={props.contextData}
                                    className='uxp-designer-data-source-selector'
                                    designer={props.designer}
                                />


                                <Checkbox
                                    checked={s?.excludeFields || false}
                                    onChange={(v) => {

                                        const _sheet = Object.assign({}, { ..._props.sheets[i], excludeFields: v })
                                        jp.apply(_props, `$.sheets[${i}]`, () => _sheet)
                                        props.onChange(_props)
                                    }}
                                    label='Exclude Field from export'
                                />

                                {s?.excludeFields ? <>
                                    <Label>Exclude fields</Label>
                                    <small>If empty all the fields will be exported</small>
                                    <MultiSelect
                                        selected={s.fieldsToExclude || []}
                                        onChange={v => {

                                            const _sheet = Object.assign({}, { ..._props.sheets[i], fieldsToExclude: v })
                                            jp.apply(_props, `$.sheets[${i}]`, () => _sheet)
                                            props.onChange(_props)
                                        }}
                                        options={pv.map(v => ({ label: v, value: v }))}
                                        className="widget-designer-input"
                                        showEndOfContent={false}
                                    />
                                </> : <>

                                    <Label>Include fields</Label>
                                    <small>If empty all the fields will be exported</small>
                                    <MultiSelect
                                        selected={s.fieldsToInclude || []}
                                        onChange={v => {

                                            const _sheet = Object.assign({}, { ..._props.sheets[i], fieldsToInclude: v })
                                            jp.apply(_props, `$.sheets[${i}]`, () => _sheet)
                                            props.onChange(_props)
                                        }}
                                        options={pv.map(v => ({ label: v, value: v }))}
                                        className="widget-designer-input"
                                        showEndOfContent={false}
                                    />
                                </>
                                }
                            </div>
                        })
                    }
                </div>

                <Button
                    className="new-option"
                    title="Add Sheet"
                    icon={"/Resources/Lucy/lucy-dashboard/assets/images/plus-rounded.svg"}
                    iconPosition="right"
                    onClick={addSheet}
                />

            </Property>

        </PropertyGroup>
    </PropertyContainer>
}

const WDFilterPanel: React.FunctionComponent<IWDFilterPanelProps> = (props) => {

    let [parentStyles, setParentStyles] = React.useState<any>({})
    let [childStyles, setChildStyles] = React.useState<any>({})
    let [show, setShow] = React.useState(false)
    // let [data, setData] = React.useState({})

    const buttonRef = React.useRef(null)
    const filterPanelContainerRef = React.useRef(null)

    let filterProps = props?.uiProps?.filter || { fillContainer: false, enableClear: true, enableExport: true, fields: [] }
    let exportProps = props?.uiProps?.export || { sheets: [] }

    const exportToExcel = useExportToExcel;


    React.useEffect(() => {
        document.addEventListener("click", (e) => handleClick(e))

        return () => {
            document.removeEventListener("click", (e) => handleClick(e))
        }
    }, [])

    React.useEffect(() => {
        handleButtonClick()
    }, [filterProps.fillContainer])

    React.useEffect(() => {
        if (show) {
            if (props.onOpenPanel) props.onOpenPanel()
        }
        else {
            if (props.onClosePanel) props.onClosePanel()
        }
    }, [show])

    function closePanel() {
        setShow(false)
    }

    function clearFilters() {
        props.onClearFilters()
    }

    // on click modal or backdrop 
    const handleClick = (e: MouseEvent) => {
        if (e.target == filterPanelContainerRef.current) {
            closePanel()
        }
    }

    function handleButtonClick() {

        const buttonDetails = buttonRef.current.getBoundingClientRect();
        const containerDetails = props.containerRef.current.getBoundingClientRect();

        let parentStyles: any = {}
        let childStyles: any = {
            top: buttonDetails?.y + window.scrollY - 10,
            right: window.innerWidth - buttonDetails.right - 10,
        }

        if (props?.designer?.mode == 'preview') {
            parentStyles = {
                top: containerDetails.top + window.scrollY,
                left: containerDetails.left,
                width: containerDetails.width,
                height: containerDetails.height,
            }

            childStyles = {
                top: 0,
                right: 0
            }

            if (filterProps.fillContainer) {
                childStyles = {
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                }
            }
        }
        else if (filterProps.fillContainer) {
            childStyles = {
                top: containerDetails.top + window.scrollY,
                left: containerDetails.left,
                width: containerDetails.width,
                height: containerDetails.height
            }
        }

        setParentStyles(parentStyles);
        setChildStyles(childStyles);
    }

    function processData(data: any[], sheet: IExportSheet) {

        if (sheet?.excludeFields) {
            if (sheet?.fieldsToExclude?.length == 0) return data
            const updated = data.map(d => {
                const _row = { ...d }
                sheet.fieldsToExclude.forEach(f => delete _row[f])
                return _row
            })

            return updated
        }

        if (sheet?.fieldsToInclude?.length > 0) {
            const updated = data.map(d => {
                const _row: any = {}
                sheet.fieldsToInclude.forEach(f => { _row[f] = d[f] })
                return _row
            })

            return updated
        }

        return data
    }

    async function prepareData(sheets: IExportSheet[]) {
        const exportData: { [sheet: string]: any[] } = {}

        for (const sheet of sheets) {
            try {
                const data = await executeAndBind(props.uxpContext, sheet.binding, props?.uiProps?.datasources || [], {}, props.data)
                if (data) {
                    exportData[sheet.name] = processData(data, sheet)
                }
            } catch (e) {
                console.log('unable to get the data to export ', sheet, e)
            }
        }
        return exportData
    }
    async function exportData() {
        if (!exportProps?.sheets || exportProps?.sheets?.length == 0) return
        const exportData = await prepareData(exportProps.sheets)

        exportToExcel(exportData, props.exportFileName || `${props?.uiProps?.__widget_name__ || 'Data'}-${format((new Date()), 'yyyy-MM-dd-HH-mm')}`)
    }

    function renderContent() {
        let styles: any = {}
        if (filterProps.fillContainer) styles.height = '100%'

        return <div className={classNames("uxp-filter-panel-container", {'wd-design-mode': props?.designer?.mode })} ref={filterPanelContainerRef} style={parentStyles}>
            <div className={classNames("uxp-filter-panel")}
                style={childStyles}
            >
                <div className="uxp-filter-header">

                    <div className="title">
                        <div className="icon"></div>
                        <div className="text">Filters</div>
                    </div>

                    <div className="icon-container" onClick={closePanel}>
                        <div className="icon"></div>
                    </div>

                </div>

                <div className="uxp-filter-body" style={styles} >
                    <FieldContainer
                        designer={props.designer}
                        uxpContext={props.uxpContext}
                        contextData={props.data}
                        dataKey="filters"
                        onChangeData={data => {

                            props.onChangeFilters(data || {})
                        }}
                        uiProps={props.uiProps}
                        propPath={['filter', 'fields']}
                        onChangeFields={(fields) => {
                            let _uiProps = Object.assign({}, props.uiProps)
                            if (!_uiProps.filter) _uiProps.filter = Object.assign({}, filterProps)
                            _uiProps.filter.fields = fields
                            props?.designer?.setUIProps(_uiProps)
                        }}
                    />
                </div>

                <div className={"uxp-filter-footer"}>
                    <button className={classNames("clear-filter", { "active": filterProps.enableClear })}
                        onClick={clearFilters}
                    >
                        <div className="icon-container">
                            <div className="icon"></div>
                        </div>
                        <div className="text">Clear</div>
                    </button>
                </div>

                {filterProps?.enableExport &&
                    <SelectableItem
                        designer={props.designer}
                        props={[
                            {
                                id: addToPath('export'),
                                value: exportProps,
                                definition: {
                                    defaults: { sheets: [] },
                                    render: ExportPropertyEditor
                                }
                            }
                        ]}
                    >
                        <div className="wd-filter-panel-export-section">
                            <Label>Export data</Label>

                            <Button
                                title='Export'
                                onClick={() => {
                                    if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) return
                                    exportData()
                                }}
                            />
                        </div>
                    </SelectableItem>
                }
            </div>
        </div>
    }

    return <>

        <SelectableItem
            designer={props.designer}
            props={[
                {
                    id: addToPath('filter'),
                    value: filterProps,
                    definition: {
                        defaults: { fillContainer: false, enableClear: true, enableExport: true },
                        render: PropertyEditor
                    }
                }
            ]}
        >

            <div className="filter-panel-btn"
                onClick={() => {
                    handleButtonClick()
                    setShow(true)
                }}
                ref={buttonRef}
            >
                <div className="icon-container">
                    <div className="icon"></div>
                </div>
            </div>
        </SelectableItem>

        <CSSTransition
            in={show}
            timeout={201.25}
            classNames={"f-fade"}
            unmountOnExit={true}
        >
            <PortalContainer>
                {renderContent()}
            </PortalContainer>
        </CSSTransition>
    </>
}

export default WDFilterPanel